/**
 * 离线缓存插件
 */

import type { Plugin } from '../../core/collector'
import type { Collector } from '../../core/collector'
import type { EventInfo } from '../../@types'
import { StorageWrapper } from '../../core/storage'
import { isBrowser } from '../../core/utils'

/**
 * 缓存插件类
 */
export class CachePlugin implements Plugin {
  name = 'cache'
  version = '1.0.0'

  private collector!: Collector
  private storage: StorageWrapper

  constructor() {
    this.storage = new StorageWrapper('local')
  }

  install(collector: Collector): void {
    this.collector = collector

    const cacheConfig = this.collector.config.get('cache')
    if (cacheConfig?.enable === false) {
      return
    }

    // 加载缓存数据
    this.loadCache()

    // 监听队列变化，自动保存
    this.watchQueue()
  }

  /**
   * 监听队列变化
   */
  private watchQueue(): void {
    // 通过代理原始 push 方法来监听
    const originalPush = this.collector.queue.push.bind(this.collector.queue)

    this.collector.queue.push = (log: EventInfo, callback?: (log: EventInfo) => void) => {
      originalPush(log, (l: EventInfo) => {
        this.saveCache()
        if (callback) callback(l)
      })
    }
  }

  /**
   * 保存缓存
   */
  saveCache(): void {
    if (!isBrowser()) return

    const cacheConfig = this.collector.config.get('cache')
    if (cacheConfig?.enable === false) return

    try {
      const logs = this.collector.queue.getAll()
      const maxCacheSize = cacheConfig?.maxSize || 100
      const cacheData = logs.slice(-maxCacheSize)
      const cacheKey = cacheConfig?.key || 'event_tracker_cache'

      this.storage.setItem(cacheKey, cacheData)
    } catch (error) {
      this.collector.config.warn('缓存保存失败', error)
    }
  }

  /**
   * 加载缓存
   */
  private loadCache(): void {
    if (!isBrowser()) return

    const cacheConfig = this.collector.config.get('cache')
    if (cacheConfig?.enable === false) return

    try {
      const cacheKey = cacheConfig?.key || 'event_tracker_cache'
      const cacheData = this.storage.getItem<EventInfo[]>(cacheKey)

      if (Array.isArray(cacheData) && cacheData.length > 0) {
        cacheData.forEach((log) => this.collector.queue.push(log))
        this.collector.config.debug(`从缓存加载 ${cacheData.length} 条数据`)
      }
    } catch (error) {
      this.collector.config.warn('缓存加载失败', error)
    }
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    if (!isBrowser()) return

    const cacheConfig = this.collector.config.get('cache')
    if (cacheConfig?.enable === false) return

    try {
      const cacheKey = cacheConfig?.key || 'event_tracker_cache'
      this.storage.removeItem(cacheKey)
    } catch (error) {
      this.collector.config.warn('缓存清除失败', error)
    }
  }
}

export default CachePlugin
